// Edição da DESCRIÇÃO — o único campo editável da configuração. As regras
// continuam imutáveis (assinaturas e seguros apontam para elas); a descrição
// pode mudar porque não entra no hash de deduplicação do core.
//
// Presentational: recebe a configuração e devolve a descrição nova em
// `onSubmit`. Quem chama o gateway é a página.
import { zodResolver } from "@hookform/resolvers/zod"
import { useEffect, useMemo } from "react"
import { useForm } from "react-hook-form"
import { useTranslation } from "react-i18next"

import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form"
import { Input } from "@/components/ui/input"
import type { PaymentConfiguration } from "@/types/payment-configurations"

import {
  buildDescriptionSchema,
  DESCRIPTION_MAX_LENGTH,
  type DescriptionFormValues,
} from "../schemas"

const prefix = "pages.paymentConfigurations"

interface EditPaymentConfigurationDescriptionDialogProps {
  /** `null` mantém o diálogo fechado. */
  configuration: PaymentConfiguration | null
  isSubmitting: boolean
  onSubmit: (description: string) => void
  onClose: () => void
}

export const EditPaymentConfigurationDescriptionDialog = ({
  configuration,
  isSubmitting,
  onSubmit,
  onClose,
}: EditPaymentConfigurationDescriptionDialogProps) => {
  const { t } = useTranslation()
  const schema = useMemo(
    () =>
      buildDescriptionSchema(
        t(`${prefix}.validation.descriptionTooLong`, {
          max: DESCRIPTION_MAX_LENGTH,
        })
      ),
    [t]
  )
  const form = useForm<DescriptionFormValues>({
    resolver: zodResolver(schema),
    defaultValues: { description: "" },
    // Mesmo modo do formulário de criação: o limite aparece ao sair do campo.
    mode: "onBlur",
    reValidateMode: "onChange",
  })
  const { reset } = form
  const isDirty = form.formState.isDirty

  // Cada abertura começa da descrição salva — não do rascunho anterior.
  useEffect(() => {
    if (configuration) {
      reset({
        description: configuration.description ?? "",
      })
    }
  }, [configuration, reset])

  return (
    <Dialog
      open={configuration !== null}
      onOpenChange={(open) => {
        if (!open && !isSubmitting) onClose()
      }}
    >
      {/* Com texto alterado, Esc e clique fora não fecham — perder o que foi
          digitado por acidente. Cancelar continua fechando: é intenção. */}
      <DialogContent
        onEscapeKeyDown={(event) => {
          if (isDirty) event.preventDefault()
        }}
        onInteractOutside={(event) => {
          if (isDirty) event.preventDefault()
        }}
      >
        <DialogHeader>
          <DialogTitle>
            {t(`${prefix}.editDescription.title`)}
          </DialogTitle>
          <DialogDescription>
            {t(`${prefix}.editDescription.description`)}
          </DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form
            noValidate
            className="flex flex-col gap-4"
            onSubmit={form.handleSubmit((values) =>
              onSubmit(values.description)
            )}
          >
            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    {t(`${prefix}.editDescription.label`)}
                  </FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      autoComplete="off"
                      maxLength={DESCRIPTION_MAX_LENGTH}
                      placeholder={t(
                        `${prefix}.form.descriptionPlaceholder`
                      )}
                    />
                  </FormControl>
                  <FormDescription>
                    {t(`${prefix}.editDescription.hint`)}
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                disabled={isSubmitting}
                onClick={onClose}
              >
                {t(`${prefix}.editDescription.cancel`)}
              </Button>
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting
                  ? t(`${prefix}.editDescription.saving`)
                  : t(`${prefix}.editDescription.save`)}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  )
}
